// client/src/components/react-public/Checkout.jsx

import {
  useSelector,
} from 'react-redux';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  useTheme,
} from '@mui/material';

import {
  Formik,
} from 'formik';

import {
  useMemo,
  useState,
} from 'react';

import * as yup from 'yup';

import {
  tokens,
} from '../../theme';

import Payment
  from './store-components/Payment';

import Shipping
  from './store-components/Shipping';

import Navbar
  from './global/Navbar';

import Footer
  from './global/Footer';

import {
  useCreateOrderMutation,
  useGetCheckoutProfileQuery,
} from '../../features/orders/ordersApiSlice';

import {
  selectCurrentToken,
} from '../../features/auth/authSlice';


const Checkout = () => {
  const [
    activeStep,
    setActiveStep,
  ] =
    useState(0);


  const [
    checkoutError,
    setCheckoutError,
  ] =
    useState('');


  const cart =
    useSelector(
      (state) =>
        state.cart.cart
    );


  const token =
    useSelector(
      selectCurrentToken
    );


  const isLoggedIn =
    Boolean(token);


  const {
    data:
    checkoutProfile,

    isLoading:
    isProfileLoading,

    isError:
    isProfileError,

    error:
    profileError,
  } =
    useGetCheckoutProfileQuery(
      undefined,
      {
        skip:
          !isLoggedIn,
      }
    );


  const [
    createOrder,
    {
      isLoading:
      isCreatingCheckout,
    },
  ] =
    useCreateOrderMutation();


  const theme =
    useTheme();

  const colors =
    tokens(
      theme.palette.mode
    );


  /*
   * Existing accounts don't need the
   * contact/account-creation step.
   */
  const steps =
    isLoggedIn
      ? [
        'Billing & shipping',
      ]
      : [
        'Billing & shipping',
        'Contact & account',
      ];


  const isFirstStep =
    activeStep === 0;

  const isSecondStep =
    activeStep === 1;


  const formInitialValues =
    useMemo(
      () =>
        buildInitialValues(
          isLoggedIn
            ? checkoutProfile
            : null
        ),
      [
        isLoggedIn,
        checkoutProfile,
      ]
    );


  const startStripeCheckout =
    async (
      values,
      actions
    ) => {
      try {
        const products =
          cart.map(
            (item) => ({
              id:
                item._id ||
                item.id ||
                item.cartId,

              quantity:
                Math.max(
                  1,
                  Number(
                    item.count
                  ) || 1
                ),
            })
          );


        if (
          !products.length ||
          products.some(
            (product) =>
              !product.id
          )
        ) {
          throw new Error(
            'One or more cart products are invalid.'
          );
        }


        const checkoutData = {
          /*
           * For authenticated customers these
           * were loaded from their server-side
           * account profile.
           *
           * The backend also independently
           * overrides account/email behavior
           * when a valid JWT is present.
           */
          email:
            values.email.trim(),

          phoneNumber:
            values
              .phoneNumber
              .trim(),

          organizationName:
            values
              .organizationName
              .trim(),


          /*
           * Existing accounts should NEVER
           * request another account.
           */
          createAccount:
            isLoggedIn
              ? false
              : Boolean(
                values.createAccount
              ),

          accountAuthMethod:
            isLoggedIn
              ? 'manual'
              : values
                .accountAuthMethod,

          googleCredential:
            isLoggedIn
              ? undefined
              : (
                values
                  .googleCredential ||
                undefined
              ),

          billingAddress:
            values
              .billingAddress,

          shippingAddress:
            values
              .shippingAddress,

          products,
        };


        const session =
          await createOrder(
            checkoutData
          ).unwrap();


        if (!session?.url) {
          throw new Error(
            'The payment provider did not return a checkout URL.'
          );
        }


        window.location.assign(
          session.url
        );
      } catch (error) {
        console.error(
          'Failed to create Stripe Checkout Session:',
          error
        );


        setCheckoutError(
          error?.data
            ?.message ||
          error?.message ||
          'Unable to start payment. Please try again.'
        );


        actions
          .setSubmitting(
            false
          );
      }
    };


  const handleFormSubmit =
    async (
      values,
      actions
    ) => {
      setCheckoutError(
        ''
      );


      /*
       * Formik's values object isn't guaranteed
       * to reflect setFieldValue immediately,
       * so construct the actual submitted values
       * locally as well.
       */
      let checkoutValues =
        values;


      if (
        isFirstStep &&
        values
          .shippingAddress
          .isSameAddress
      ) {
        const shippingAddress = {
          ...values
            .billingAddress,

          isSameAddress:
            true,
        };


        await actions
          .setFieldValue(
            'shippingAddress',
            shippingAddress
          );


        checkoutValues = {
          ...values,

          shippingAddress,
        };
      }


      /*
       * Anonymous buyer:
       *
       * continue to Contact & Account.
       */
      if (
        isFirstStep &&
        !isLoggedIn
      ) {
        actions
          .setTouched(
            {}
          );

        actions
          .setSubmitting(
            false
          );

        setActiveStep(
          1
        );

        return;
      }


      /*
       * Logged-in buyer:
       *
       * Billing & shipping is the only step,
       * so go directly to Stripe.
       *
       * Anonymous buyer:
       *
       * reaches here from step 2.
       */
      await startStripeCheckout(
        checkoutValues,
        actions
      );
    };


  /*
   * Don't initialize Formik with blanks and
   * then reset it once account data arrives.
   */
  if (
    isLoggedIn &&
    isProfileLoading
  ) {
    return (
      <Box
        width="100%"
        minHeight="100vh"
      >
        <Navbar />

        <Box
          minHeight="60vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress
            sx={{
              color:
                colors.greenAccent[400],
            }}
          />
        </Box>

        <Footer />
      </Box>
    );
  }


  if (
    isLoggedIn &&
    isProfileError
  ) {
    return (
      <Box
        width="100%"
        minHeight="100vh"
      >
        <Navbar />

        <Box
          width={{
            xs:
              '92%',

            md:
              '80%',
          }}
          maxWidth="1100px"
          m="100px auto"
        >
          <Alert
            severity="error"
          >
            {profileError
              ?.data
              ?.message ||
              'Unable to load your saved checkout information.'}
          </Alert>
        </Box>

        <Footer />
      </Box>
    );
  }


  return (
    <Box
      width="100%"
    >
      <Navbar />


      <Box
        width={{
          xs:
            '92%',

          md:
            '80%',
        }}
        maxWidth="1100px"
        m="100px auto"

        sx={{
          fontFamily:
            'Poppins, sans-serif',

          '& .MuiTypography-root': {
            fontFamily:
              'Poppins, sans-serif',
          },

          '& .MuiButton-root': {
            fontFamily:
              'Poppins, sans-serif',
          },
        }}
      >
        <Stepper
          activeStep={
            activeStep
          }
          sx={{
            mb:
              4,
          }}
        >
          {steps.map(
            (label) => (
              <Step
                key={
                  label
                }
              >
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color:
                        colors.grey[300],

                      fontFamily:
                        'Poppins, sans-serif',
                    },

                    '& .MuiStepLabel-label.Mui-active': {
                      color:
                        colors.greenAccent[300],

                      fontWeight:
                        800,
                    },

                    '& .MuiStepLabel-label.Mui-completed': {
                      color:
                        colors.greenAccent[400],
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            )
          )}
        </Stepper>


        {checkoutError && (
          <Alert
            severity="error"
            sx={{
              mb:
                3,
            }}
          >
            {checkoutError}
          </Alert>
        )}


        <Formik
          onSubmit={
            handleFormSubmit
          }

          initialValues={
            formInitialValues
          }

          validationSchema={
            checkoutSchema[
            activeStep
            ]
          }
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form
              onSubmit={
                handleSubmit
              }
            >
              {isFirstStep && (
                <Shipping
                  values={
                    values
                  }

                  errors={
                    errors
                  }

                  touched={
                    touched
                  }

                  handleBlur={
                    handleBlur
                  }

                  handleChange={
                    handleChange
                  }

                  setFieldValue={
                    setFieldValue
                  }
                />
              )}


              {/*
               * Existing users NEVER see this.
               */}
              {!isLoggedIn &&
                isSecondStep && (
                  <Payment
                    existingAccount={
                      isLoggedIn
                    }
                    values={
                      values
                    }

                    errors={
                      errors
                    }

                    touched={
                      touched
                    }

                    handleBlur={
                      handleBlur
                    }

                    handleChange={
                      handleChange
                    }

                    setFieldValue={
                      setFieldValue
                    }
                  />
                )}


              <Box
                display="flex"
                flexDirection={{
                  xs:
                    'column-reverse',

                  sm:
                    'row',
                }}
                justifyContent="space-between"
                gap={
                  2
                }
                mt={
                  3
                }
              >
                {!isFirstStep && (
                  <Button
                    fullWidth
                    type="button"

                    disabled={
                      isCreatingCheckout
                    }

                    onClick={
                      () =>
                        setActiveStep(
                          (step) =>
                            step - 1
                        )
                    }

                    sx={{
                      color:
                        'white',

                      border:
                        '1px solid rgba(255,255,255,0.16)',

                      borderRadius:
                        '14px',

                      padding:
                        '16px 28px',

                      fontWeight:
                        800,
                    }}
                  >
                    Back
                  </Button>
                )}


                <Button
                  fullWidth
                  type="submit"

                  disabled={
                    isSubmitting ||
                    isCreatingCheckout ||
                    cart.length === 0
                  }

                  sx={{
                    backgroundColor:
                      colors.primary[500],

                    color:
                      colors.greenAccent[300],

                    borderRadius:
                      '14px',

                    padding:
                      '16px 28px',

                    fontWeight:
                      900,

                    fontSize:
                      '1rem',

                    '&:hover': {
                      backgroundColor:
                        colors.primary[400],
                    },
                  }}
                >
                  {isCreatingCheckout ? (
                    <CircularProgress
                      size={
                        24
                      }
                      color="inherit"
                    />
                  ) : (
                    isLoggedIn ||
                      isSecondStep
                      ? 'Continue to secure payment'
                      : 'Continue'
                  )}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>


      <Footer />
    </Box>
  );
};


const emptyAddress = {
  firstName:
    '',

  lastName:
    '',

  country:
    '',

  street1:
    '',

  street2:
    '',

  city:
    '',

  state:
    '',

  zipCode:
    '',
};


const ADDRESS_FIELDS = [
  'firstName',
  'lastName',
  'country',
  'street1',
  'street2',
  'city',
  'state',
  'zipCode',
];


const normalizeAddress =
  (address = {}) => ({
    ...emptyAddress,

    firstName:
      address.firstName ||
      '',

    lastName:
      address.lastName ||
      '',

    country:
      address.country ||
      '',

    street1:
      address.street1 ||
      '',

    street2:
      address.street2 ||
      '',

    city:
      address.city ||
      '',

    state:
      address.state ||
      '',

    zipCode:
      address.zipCode ||
      '',
  });


const addressHasData =
  (address) =>
    ADDRESS_FIELDS.some(
      (field) =>
        Boolean(
          String(
            address?.[field] ||
            ''
          ).trim()
        )
    );


const addressesMatch =
  (
    first,
    second
  ) =>
    ADDRESS_FIELDS.every(
      (field) =>
        String(
          first?.[field] ||
          ''
        )
          .trim()
          .toLowerCase() ===
        String(
          second?.[field] ||
          ''
        )
          .trim()
          .toLowerCase()
    );


const buildInitialValues =
  (profile) => {
    if (!profile) {
      return {
        billingAddress: {
          ...emptyAddress,
        },

        shippingAddress: {
          ...emptyAddress,

          isSameAddress:
            true,
        },

        email:
          '',

        phoneNumber:
          '',

        organizationName:
          '',

        createAccount:
          false,

        googleCredential:
          '',

        accountAuthMethod:
          'manual',
      };
    }


    const billingAddress =
      normalizeAddress(
        profile.billingAddress
      );


    let shippingAddress =
      normalizeAddress(
        profile.shippingAddress
      );


    /*
     * If the account doesn't have a distinct
     * shipping address saved, use billing.
     */
    const hasSavedShipping =
      addressHasData(
        shippingAddress
      );


    const isSameAddress =
      !hasSavedShipping ||
      addressesMatch(
        billingAddress,
        shippingAddress
      );


    if (
      isSameAddress
    ) {
      shippingAddress = {
        ...billingAddress,
      };
    }


    return {
      billingAddress,

      shippingAddress: {
        ...shippingAddress,

        isSameAddress,
      },

      email:
        profile.email ||
        '',

      phoneNumber:
        profile.phoneNumber ||
        '',

      organizationName:
        profile.organizationName ||
        '',


      /*
       * Existing account: never run signup.
       */
      createAccount:
        false,

      googleCredential:
        '',

      accountAuthMethod:
        'manual',
    };
  };


const addressSchema =
  yup.object().shape({
    firstName:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    lastName:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    country:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    street1:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    street2:
      yup
        .string()
        .trim(),

    city:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    state:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    zipCode:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),
  });


const checkoutSchema = [
  yup.object().shape({
    billingAddress:
      addressSchema,

    shippingAddress:
      yup.object().shape({
        isSameAddress:
          yup.boolean(),

        firstName:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        lastName:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        country:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        street1:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        street2:
          yup
            .string()
            .trim(),

        city:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        state:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),

        zipCode:
          yup.string().when(
            'isSameAddress',
            {
              is:
                false,

              then:
                yup
                  .string()
                  .trim()
                  .required(
                    'Required'
                  ),
            }
          ),
      }),
  }),


  /*
   * Only anonymous checkout ever uses
   * this validation schema.
   */
  yup.object().shape({
    email:
      yup
        .string()
        .trim()
        .email(
          'Enter a valid email address'
        )
        .required(
          'Required'
        ),

    phoneNumber:
      yup
        .string()
        .trim()
        .required(
          'Required'
        ),

    createAccount:
      yup.boolean(),

    organizationName:
      yup
        .string()
        .trim()
        .when(
          'createAccount',
          {
            is:
              true,

            then:
              yup
                .string()
                .trim()
                .required(
                  'Enter your business or organization name'
                ),
          }
        ),
  }),
];


export default Checkout;